import { sortBy } from "lodash";
import moment from "moment";
import * as React from "react";
import { CountyProjectContainerState, CountyProjectContext, CountyProjectContextProps } from "@app/projects/CountyProjectContext";
import { AttestationRateDays, ProjectRunConfigurationStatus, ProjectRunConfigurationType, TimeFrequency } from "@app/projects/enumerators";
import { DisplayOptions } from "@app/projects/network-assessment/DisplayOptions";
import { AdequacyFilters } from "@app/projects/network-assessment/adequacy";
import ProjectOptions from "@app/projects/projectOptions";
import ProjectRunConfiguration from "@app/projects/projectRunConfiguration";
import { ProjectRunConfigurationResult } from "@app/projects/projectRunConfigurationResult";
import { AdequacyTrendChartItem } from "@app/projects/results/common";
import { FlyoutLink } from "@app/shared/Flyouts";
import { Toggle } from "@app/shared/Toggle";
import { TrendChartItemType } from "@app/velvet/enumerators";
import { NewActionMenu, NewActionMenuItem } from "@app/shared/action-menu";
import CheckboxDropdown from "@app/shared/checkbox-dropdown/CheckboxDropdown";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { ViewableFieldsContextProps, withViewableFields } from "@app/viewable-fields/ViewableFieldsContext";
import { TableType } from "@app/viewable-fields/viewableFields";
import { ToggleButton } from "@app/shared/toggle-button/ToggleButton";
import { FeatureFlagContext, FeatureFlagContextProps } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { AdequacySubmoduleEnum } from "@app/projects/network-assessment/adequacySubmoduleEnum";

type OptionsProps = ViewableFieldsContextProps & {
  defaultProjectOptionsOpen: boolean;
  disabled: boolean;
  isReadOnly: boolean;
  showGraphicalArea: boolean;
  onShowGraphicalAreaClick: () => void;
  hasProspectOrMarketProviders: boolean;
  onSelectedSubmoduleChange: (newView: AdequacySubmoduleEnum) => void;
  selectedSubmodule: AdequacySubmoduleEnum;
  showTrendChartToggle: boolean;
  adequacyFilters?: AdequacyFilters;
};

type OptionsContentProps = OptionsProps & CountyProjectContextProps & FeatureFlagContextProps;

interface OptionsState {
  displayOptionsDirty: boolean;
  projectOptionsDirty: boolean;
  selectedMemberSetAliases: Array<DropdownItem<ProjectRunConfigurationType>>;
  triggerClose: () => void;
}

const Options: React.FC<React.PropsWithChildren<OptionsProps>> = (props: OptionsProps) => {
  return (
    <FeatureFlagContext.Consumer>
      {({ enabledFeatureFlags }) => (
        <CountyProjectContext.Consumer>
          {(contextProps) => (
            <OptionsContent {...props} {...contextProps} enabledFeatureFlags={enabledFeatureFlags} />
          )}
        </CountyProjectContext.Consumer>
      )}
    </FeatureFlagContext.Consumer>
  );
};

class OptionsContent extends React.Component<OptionsContentProps, OptionsState> {
  private pendingMembershipChange: Array<DropdownItem<ProjectRunConfigurationType>> | null = null;
  private buttonToggleOptions: { [key: string]: string } = { trendChart: "Trend Chart", viewMap: "View Map", };

  constructor(props: OptionsContentProps) {
    super(props);

    this.state = {
      displayOptionsDirty: false,
      projectOptionsDirty: false,
      selectedMemberSetAliases: [],
      triggerClose: null,
    };

    if (this.props.enabledFeatureFlags.has(Features.GAP_MANAGEMENT_DASHBOARD)) {
      this.buttonToggleOptions.gapManagement = "Gap Management";
    }
  }

  componentDidUpdate(prevProps: OptionsContentProps) {
    if (
      !!this.pendingMembershipChange && this.pendingMembershipChange.length > 0
      && prevProps.adequacyResult?.aggregateDetails !== this.props.adequacyResult.aggregateDetails
      && this.props.adequacyResult?.aggregateDetails?.length > 0
      && !this.props.loadingResults && !this.props.loading
    ) {
      this.processPendingMembershipChange();
    }

    if (prevProps.enabledFeatureFlags !== this.props.enabledFeatureFlags) {
      if (this.props.enabledFeatureFlags.has(Features.GAP_MANAGEMENT_DASHBOARD)) {
        this.buttonToggleOptions.gapManagement = "Gap Management";
      } else {
        delete this.buttonToggleOptions.gapManagement;
      }
    }
  }

  private processPendingMembershipChange = () => {
    if (this.pendingMembershipChange) {
      this.processMembershipChange(this.pendingMembershipChange, this.props.setDualMembershipMode);
      this.pendingMembershipChange = null;
    }
  }

  private processMembershipChange = (selectedItems: Array<DropdownItem<ProjectRunConfigurationType>>, setDualMembershipMode: (dualMembershipMode: boolean, projectRunConfigurationType?: ProjectRunConfigurationType, primaryConfigurationId?: number, compareToRunConfigurationId?: number, forceLoading?: boolean) => void) => {
    switch (selectedItems.length) {
      case 1:
        setDualMembershipMode(false, selectedItems[0].key);
        break;
      case 2:
        setDualMembershipMode(true);
        this.props.onSelectedSubmoduleChange(AdequacySubmoduleEnum.TrendChart);
        break;
      default:
        return;
    }
  }

  public render() {
    if (!this.props.runConfiguration) { //nothing to display if there's no run configuration
      return null;
    }

    const { compareToRunConfiguration, setRunConfiguration, adequacyTrendChartData, module, project, runConfiguration, runFrequency, attestationRateDays, adequacyResult, accuracyResult, regionFilters, isDualMembershipMode, setDualMembershipMode, setLoading } = this.props;

    //Whether or not we're currently comparing to a run configuration
    const isComparingResults = !!compareToRunConfiguration;
    //The priorRunConfigurationId to set when the toggle is clicked
    const priorRunConfigurationId = isComparingResults
      ? undefined
      : this.getCompareToRunConfigurationId(adequacyTrendChartData, runConfiguration);
    //Whether or not we should show the toggle at all
    const shouldShowCompareToggle = (isComparingResults || priorRunConfigurationId) && !isDualMembershipMode && !this.props.cmsCreditIsApplying && module !== "Accuracy";

    const haveResultsToRender: boolean = !!runConfiguration && ((!!adequacyResult && !!adequacyResult.details && adequacyResult.details.length > 0) || (!!accuracyResult && !!accuracyResult.details && accuracyResult.details.length > 0));

    const isLowestGranularity: boolean = !!regionFilters.specialty && (!!regionFilters.county || !!regionFilters.state);
    const viewablePanels: TableType[] = [];
    let showDisplayOptions = false;

    if (haveResultsToRender && isLowestGranularity && !isComparingResults) {
      showDisplayOptions = true;

      if (module === "Accuracy") {
        viewablePanels.push(TableType.AccuracyProviders);
      } else {
        viewablePanels.push(TableType.NetworkProviders);

        if (runConfiguration.showNextClosestNetworkProviders && !this.props.runConfiguration.isProjected && !this.props.runConfiguration.isProjectedTermed) {
          viewablePanels.push(TableType.NextClosestNetworkProviders);
        }
        viewablePanels.push(TableType.ProviderTerminations);

        if (runConfiguration.prospectProviderSetId && runConfiguration.prospectProviderSetVersionId) {
          viewablePanels.push(TableType.ProspectProviders);
        }
        if (runConfiguration.showNextClosestNetworkProviders && runConfiguration.prospectProviderSetId && runConfiguration.prospectProviderSetVersionId && !this.props.hasProspectOrMarketProviders && !this.props.runConfiguration.isProjected && !this.props.runConfiguration.isProjectedTermed) {
          viewablePanels.push(TableType.NextClosestProspectProviders);
        }
        if (runConfiguration.useMarketProvidersForImpact) {
          viewablePanels.push(TableType.MarketProviders);
        }
        if (runConfiguration.showNextClosestNetworkProviders && runConfiguration.useMarketProvidersForImpact && !this.props.hasProspectOrMarketProviders && !this.props.runConfiguration.isProjected && !this.props.runConfiguration.isProjectedTermed) {
          viewablePanels.push(TableType.NextClosestMarketProviders);
        }
      }
    }

    const memberSetDropdownItems = this.getMemberSets(project, runConfiguration);
    const selectedMemberAliases = this.getSelectedMemberSets(runConfiguration, isDualMembershipMode, compareToRunConfiguration);

    const timeViewDropdownItems: Array<DropdownItem<TimeFrequency>> = [
      {
        key: TimeFrequency.All,
        name: "Refreshes",
      },
      {
        key: TimeFrequency.Monthly,
        name: "Monthly Trends",
      },
    ];

    const AttestationRateDaysDropdownItems: Array<DropdownItem<AttestationRateDays>> = [
      {
        key: AttestationRateDays.Ninety,
        name: `Last ${AttestationRateDays.Ninety} Days`,
      },
      {
        key: AttestationRateDays.ThreeSixtyFive,
        name: `Last ${AttestationRateDays.ThreeSixtyFive} Days`,
      },
    ];

    const shouldDisable = (runConfiguration && runConfiguration.status === ProjectRunConfigurationStatus.Failed) ||
      (project.isCompanionRunning && !this.props.isReadOnly) ||
      !runConfiguration.companionProjectRunConfigurationId ||
      !project.projectRunConfigurations.find((prc) => prc.projectRunConfigurationId ===
        runConfiguration.companionProjectRunConfigurationId);

    const submoduleSelection = (
      <ToggleButton
        buttons={this.buttonToggleOptions}
        disabled={isComparingResults}
        onClick={this.props.onSelectedSubmoduleChange}
        selectedButton={this.props.selectedSubmodule}
        size="small"
        selectedButtonType="primary"
        testId="adequacy-submodule-selection"
        groupLabel="Displayed Adequacy Information"
      />
    );

    return (
      <div className="row overview-options pt-3 d-flex justify-content-between">
        <div>
          {runConfiguration &&
            project.projectRunConfigurations.some((prc) => !!prc.companionProjectRunConfigurationId) &&
            runConfiguration.memberSetAlias &&
            module !== "Accuracy" &&
            !(!isDualMembershipMode && compareToRunConfiguration) && (
              <CheckboxDropdown
                buttonText={isDualMembershipMode ? "Display: Both Memberships" : `Display: ${runConfiguration.memberSetAlias}`}
                cancelText="Cancel"
                disabled={shouldDisable}
                disableEmptySelection={true}
                loading={this.props.disabled && !this.props.isReadOnly}
                items={memberSetDropdownItems}
                onSubmit={(selectedItems: Array<DropdownItem<ProjectRunConfigurationType>>) => this.handleMembershipChange(selectedItems, setDualMembershipMode, setLoading)}
                selectedItems={selectedMemberAliases}
                submitText="Submit"
              />
            )}
        </div>

        <div>
          <div className="d-flex justify-content-end align-items-center flex-wrap flex-lg-nowrap">
            {module === 'Accuracy' && runConfiguration && runConfiguration.status !== ProjectRunConfigurationStatus.Failed &&
              (
                <NewActionMenu
                  buttonText={`Attestation Rate: ${AttestationRateDaysDropdownItems.find((item) => item.key === attestationRateDays).name}`}
                  buttonClassNames="qes-button-secondary"
                  key={attestationRateDays}
                  loading={this.props.disabled && !this.props.isReadOnly}
                  testId="options__attestation-rate-dropdown"
                >
                  {AttestationRateDaysDropdownItems.map((item) => {
                    const isSelected = item.key === attestationRateDays;
                    return (
                      <NewActionMenuItem
                        display={item.name}
                        isSelected={isSelected}
                        key={item.key}
                        onClick={() => this.handleAttestationRateDaysChange(item.key)}
                        testId={`options__attestation-rate-option--${item.key}`}
                      />
                    )
                  })}
                </NewActionMenu>
              )
            }
            {shouldShowCompareToggle && (
              <Toggle
                label="Compare Results"
                active={isComparingResults}
                disabled={this.props.disabled && !this.props.isReadOnly}
                onChange={
                  () => {
                    setRunConfiguration(priorRunConfigurationId, null, true)
                    this.props.onSelectedSubmoduleChange(AdequacySubmoduleEnum.TrendChart);
                  }
                }
                containerClassName="text-nowrap"
                testId="options__toggle-compare-results"
              />
            )}
            {runConfiguration && runConfiguration.status !== ProjectRunConfigurationStatus.Failed &&
              (
                <NewActionMenu
                  buttonText={`Run Frequency: ${timeViewDropdownItems.find((item) => item.key === runFrequency).name}`}
                  testId="options__run-frequency-dropdown"
                  buttonClassNames="qes-button-secondary"
                  loading={this.props.disabled && !this.props.isReadOnly}
                  key={runFrequency}
                >
                  {
                    timeViewDropdownItems.map((item) => {
                      const isSelected = item.key === runFrequency;
                      return (
                        <NewActionMenuItem
                          display={item.name}
                          isSelected={isSelected}
                          key={item.key}
                          onClick={() => this.handleRunFrequencyChange(item.key)}
                          testId="options__run-frequency-option--${item.key}"
                        />
                      )
                    })
                  }
                </NewActionMenu>
              )
            }
            {this.props.showTrendChartToggle && (
              isComparingResults ? (
                <TooltipWrapper text="Only the trend chart view may be accessed while in Compare Mode.">
                  {submoduleSelection}
                </TooltipWrapper>
              ) :
                submoduleSelection
            )}
            {showDisplayOptions && (
              <FlyoutLink
                content={{
                  body: this.renderDisplayOptions(project.projectId, viewablePanels),
                  title: "Display Options",
                }}
                modalContent={{
                  body: "You will lose unsaved changes. Continue with closing the sidebar?",
                  cancelButtonText: "Cancel",
                  confirmButtonText: "Continue",
                  title: "Warning",
                }}
                size="60"
                classNames={"qes-button-secondary text-nowrap" + (this.props.disabled && !this.props.isReadOnly ? " disabled" : "")}
                warnOnClose={this.state.displayOptionsDirty}
                testId="viewable-fields-flyout-link"
              >
                <QesIcon iconName={QesIcons.SlidersV4} isDecorative className="align-text-top mlr10" />Display&nbsp;Options
              </FlyoutLink>
            )}
            <Toggle
              label={<QesIcon iconName={QesIcons.BarChartV7} description={`${this.props.showGraphicalArea ? "Hide" : "Show"} graphs`} className="align-text-top" />}
              active={this.props.showGraphicalArea}
              disabled={this.props.disabled && !this.props.isReadOnly}
              onChange={this.props.onShowGraphicalAreaClick}
              containerClassName="ml10 vertical-separator pl10 pr15"
              testId="options__toggle-graphical-area"
            />
          </div>
        </div>

        <div className="col-12">
          <hr className="mb-0" />
        </div>
      </div >
    );
  }

  private handleRunFrequencyChange = async (selectedFrequency: TimeFrequency) => {
    if (selectedFrequency === this.props.runFrequency) {
      return;
    }

    const primaryConfigState = await this.props.setRunConfiguration(this.props.runConfiguration.projectRunConfigurationId, moment.utc(this.props.runConfiguration.dateCreated), false, selectedFrequency, true);
    let compareToRunConfigurationState: Partial<CountyProjectContainerState>;
    // if we're in compare mode, we need to track the second config
    if (this.props.compareToRunConfiguration) {
      compareToRunConfigurationState = await this.props.setRunConfiguration(this.props.compareToRunConfiguration.projectRunConfigurationId, moment.utc(this.props.compareToRunConfiguration.dateCreated), true, selectedFrequency, true);
    }

    const newState: Partial<CountyProjectContainerState> = {
      ...primaryConfigState,
      ...compareToRunConfigurationState,
      isDualMembershipMode: this.props.isDualMembershipMode,
    };

    this.props.updateRunConfigurationState(newState);
  }

  private handleAttestationRateDaysChange = async (selectedItem: AttestationRateDays) => {
    this.props.setAttestationRateDays(selectedItem);
  }

  private getCompareToRunConfigurationId(trendChartData: AdequacyTrendChartItem[], currentRunConfiguration: ProjectRunConfigurationResult): number {
    if (!trendChartData || trendChartData.length === 0 || trendChartData.length === 1 || !currentRunConfiguration || !trendChartData.some((tcd) => tcd.projectRunConfigurationId !== currentRunConfiguration.projectRunConfigurationId)) {
      return undefined;
    }

    const filteredTrendChartData = trendChartData.filter((tcd) => tcd.projectRunConfigurationId !== currentRunConfiguration.projectRunConfigurationId && !tcd.disabled);

    const latestPublishedRunConfig = filteredTrendChartData.find((tcd) => tcd.itemType === TrendChartItemType.LatestPublished);

    if (latestPublishedRunConfig) {
      //there is a latest published run configuration to compare to
      return latestPublishedRunConfig.projectRunConfigurationId;
    } else {
      //either there is no latest published run configuration, or it's already selected
      //either way, choose the most recent run configuration if any non-projected exist, otherwise return the projected run configuration
      const termedData = filteredTrendChartData.find((tcd) => tcd.itemType === TrendChartItemType.Termed);

      const projectedData = filteredTrendChartData.find((tcd) => tcd.itemType === TrendChartItemType.Projected);

      const projectedTermedData = filteredTrendChartData.find((tcd) => tcd.itemType === TrendChartItemType.ProjectedTermed);

      const nonNullProjectedDataCount = (termedData ? 1 : 0) + (projectedData ? 1 : 0) + (projectedTermedData ? 1 : 0);

      if (nonNullProjectedDataCount > 0 && filteredTrendChartData.length === nonNullProjectedDataCount) {
        //if the projected item count is equal to the filteredTrendChartData count, then there are no non-projected run configurations. Return whichever is non-null, starting with projection
        //there's only ever one projected run configuration, so this means there are no non-projected configurations available

        if (projectedData) {
          return projectedData.projectRunConfigurationId;
        }

        //if there's no projected, there must be a termed
        return termedData.projectRunConfigurationId;
      }

      //if there's a non-projected run configuration available, return the most recent non-projected. No need to check published or unpublished.
      const sortedTrendChartData = sortBy(filteredTrendChartData.filter((tcd) => tcd.itemType !== TrendChartItemType.Termed && tcd.itemType !== TrendChartItemType.Projected && tcd.itemType !== TrendChartItemType.ProjectedTermed), (tcd) => moment.utc(tcd.date)).reverse();

      return sortedTrendChartData[0] ? sortedTrendChartData[0].projectRunConfigurationId : null;
    }
  }

  private handleDisplayOptionsChange = (displayOptionsDirty: boolean) => {
    if (this.state.displayOptionsDirty !== displayOptionsDirty) {
      this.setState({ displayOptionsDirty });
    }
  }

  private renderDisplayOptions(projectId: number, viewablePanels: TableType[]) {
    return (
      <DisplayOptions
        defaultOpen={true}
        projectId={projectId}
        viewablePanels={viewablePanels}
        onChange={this.handleDisplayOptionsChange}
      />
    );
  }

  private registerTriggerClose = (triggerClose: () => void) => {
    this.setState((prevState) => ({ ...prevState, triggerClose }));
  }

  private getMemberSets = (project: ProjectOptions, runConfiguration: ProjectRunConfiguration) => {
    // We can safely assume there's a companionmembersetid
    const sets: Array<DropdownItem<ProjectRunConfigurationType>> = [];
    const companionRunConfiguration = project.projectRunConfigurations.find((config) => config.projectRunConfigurationId === runConfiguration.companionProjectRunConfigurationId);
    if (runConfiguration.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipPrimary) {
      sets.push({
        key: runConfiguration.projectRunConfigurationType,
        name: runConfiguration.memberSetAlias,
      });
      if (companionRunConfiguration) {
        sets.push({
          key: companionRunConfiguration.projectRunConfigurationType,
          name: companionRunConfiguration.memberSetAlias,
        });
      }
    } else if (companionRunConfiguration && companionRunConfiguration.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipPrimary) {
      sets.push({
        key: companionRunConfiguration.projectRunConfigurationType,
        name: companionRunConfiguration.memberSetAlias,
      });
      sets.push({
        key: runConfiguration.projectRunConfigurationType,
        name: runConfiguration.memberSetAlias,
      });
    }
    return sets;
  }

  private handleMembershipChange = async (selectedItems: Array<DropdownItem<ProjectRunConfigurationType>>, setDualMembershipMode: (dualMembershipMode: boolean, projectRunConfigurationType?: ProjectRunConfigurationType, primaryConfigurationId?: number, compareToRunConfigurationId?: number, forceLoading?: boolean) => void, setLoading: (loadingResults: boolean) => void) => {
    setLoading(true);
    this.setState({
      selectedMemberSetAliases: selectedItems,
    });

    const hasOpportunityFilter = this.props.adequacyFilters?.opportunity;

    if (hasOpportunityFilter) {

      this.pendingMembershipChange = selectedItems;

      if (this.props.adequacyResult?.aggregateDetails?.length > 0 && !this.props.loadingResults && !this.props.loading) {
        this.processPendingMembershipChange();
      }

      return;
    }

    this.processMembershipChange(selectedItems, setDualMembershipMode);
  }

  private getSelectedMemberSets = (runConfiguration: ProjectRunConfiguration, isDualMembershipMode: boolean, compareToRunConfiguration: ProjectRunConfiguration) => {
    const result: Array<DropdownItem<ProjectRunConfigurationType>> = [];

    // Single membership mode
    if (!isDualMembershipMode) {
      if (runConfiguration) {
        result.push({
          key: runConfiguration.projectRunConfigurationType,
          name: runConfiguration.memberSetAlias,
        });
      }
    } else {
      // Logic to determine order of member sets in dual membership mode
      const configs: ProjectRunConfiguration[] = [];

      //check URL parameter to see what the user selected as primary
      const urlParams = new URLSearchParams(window.location.search);
      const urlProjectRunConfigType = urlParams.get('projectRunConfigurationType');

      // If user selected Supplemental as primary
      if (urlProjectRunConfigType === ProjectRunConfigurationType.DualMembershipSupplemental) {
        const supplementalConfig = runConfiguration?.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipSupplemental ? runConfiguration : compareToRunConfiguration;
        const primaryConfig = runConfiguration?.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipPrimary ? runConfiguration : compareToRunConfiguration;

        if (supplementalConfig) configs.push(supplementalConfig);
        if (primaryConfig) configs.push(primaryConfig);
      } else {
        // Default behavior - Primary first, then Supplemental
        const primaryConfig = runConfiguration?.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipPrimary ? runConfiguration : compareToRunConfiguration;
        const supplementalConfig = runConfiguration?.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipSupplemental ? runConfiguration : compareToRunConfiguration;

        if (primaryConfig) configs.push(primaryConfig);
        if (supplementalConfig) configs.push(supplementalConfig);
      }

      // Convert configurations to dropdown items
      configs.forEach((rc) => {
        if (rc) {
          result.push({
            key: rc.projectRunConfigurationType,
            name: rc.memberSetAlias,
          });
        }
      });
    }

    return result;
  }
}

const wrappedOptions = withViewableFields(Options);
export { wrappedOptions as Options };
